import { useState, useCallback } from 'react'
import {
  Container,
  Card,
  Text,
  Stack,
  Group,
  Badge,
  Table,
  Modal,
  TextInput,
} from '@mantine/core'
import { notifications } from 'context'
import { IconCheck, IconX, IconDeviceFloppy } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/Button'
import type { StudentInvite } from 'api/arcMosaico/types'
import { submitInviteCode } from '../service/studentDashboardService'

interface Props {
  invites: StudentInvite[]
  onInviteAdded: (invite: StudentInvite) => void
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  accepted: 'green',
  declined: 'red',
}

export function StudentPendingScreen({ invites, onInviteAdded }: Props) {
  const { t } = useTranslation()
  const [codeModalOpen, setCodeModalOpen] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [codeError, setCodeError] = useState('')

  const handleSubmitCode = useCallback(async () => {
    if (!inviteCode.trim()) return
    setSubmitting(true)
    setCodeError('')
    try {
      const invite = await submitInviteCode(inviteCode.trim())
      onInviteAdded(invite)
      setCodeModalOpen(false)
      setInviteCode('')
      notifications.show({
        message: t('student_pending.toast_code_submitted'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch {
      setCodeError(t('student_pending.code_error_check'))
      notifications.show({
        message: t('student_pending.toast_code_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    } finally {
      setSubmitting(false)
    }
  }, [inviteCode, onInviteAdded, t])

  const handleCloseModal = () => {
    setCodeModalOpen(false)
    setInviteCode('')
    setCodeError('')
  }

  return (
    <>
      <Container size='xs' py='xl'>
        <Card shadow='xs' radius='md' p='lg' withBorder>
          <Stack spacing='sm' align='center'>
            <Text size='lg' weight={700}>
              {t('student_pending.title')}
            </Text>
            <Text size='sm' color='dimmed' align='center'>
              {t('student_pending.subtitle')}
            </Text>

            {invites.length === 0 ? (
              <Text size='sm' color='dimmed' align='center' py='sm'>
                {t('student_pending.no_invites')}
              </Text>
            ) : (
              <Table
                striped
                highlightOnHover
                withBorder
                verticalSpacing='xs'
                fontSize='sm'
              >
                <thead>
                  <tr>
                    <th>{t('student_pending.teacher_column')}</th>
                    <th>{t('student_pending.status_column')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(invite => (
                    <tr key={invite.id}>
                      <td>{invite.tutorName}</td>
                      <td>
                        <Badge
                          color={STATUS_COLORS[invite.status] || 'gray'}
                          variant='filled'
                          size='sm'
                        >
                          {t(`student_pending.status_${invite.status}`)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Button
              color='turquoise'
              onClick={() => setCodeModalOpen(true)}
            >
              {t('student_pending.enter_code')}
            </Button>
          </Stack>
        </Card>
      </Container>

      <Modal
        opened={codeModalOpen}
        onClose={handleCloseModal}
        title={t('student_pending.enter_code_title')}
        size='sm'
        centered
      >
        <Stack spacing='sm'>
          <TextInput
            label={t('student_pending.code_label')}
            placeholder={t('student_pending.code_placeholder')}
            value={inviteCode}
            onChange={e => {
              setInviteCode(e.currentTarget.value.toUpperCase())
              setCodeError('')
            }}
            error={codeError || undefined}
          />
          <Group position='right' mt='sm'>
            <Button variant='default' onClick={handleCloseModal}>
              {t('student_pending.cancel')}
            </Button>
            <Button
              color='turquoise'
              onClick={handleSubmitCode}
              loading={submitting}
              leftIcon={<IconDeviceFloppy size={16} />}
            >
              {t('student_pending.submit')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
